import { A04Output, A05Input, A07, A32, ApplyForm } from 'api/U002';
import { AuditionFormValue, AuditionFormValues } from './types';
import { AnswerFileType } from './const';
import { sanitizeFileName } from 'commons/utils';

type transformAuditionFormValuesToA05InputProps = Pick<
  A05Input['answer_list'][number],
  'audition_id' | 'examination_no' | 'user_id_a'
> &
  Pick<A05Input['consent_list'][number], 'audition_id' | 'consent_version'>;
/**
 * AuditionFormValuesをA05Inputに変換する
 * ファイルは著名付きURLを取得してアップロードする
 */
export const transformAuditionFormValuesToA05Input = async (
  values: AuditionFormValues,
  props: transformAuditionFormValuesToA05InputProps
): Promise<Pick<A05Input, 'answer_list' | 'consent_list'>> => {
  type ConsentAuditionFormValue = Omit<AuditionFormValue, 'answer'> & {
    answer: { file: FileList; type: typeof AnswerFileType.CONSENT };
  };
  const isConsentAnswer = (value: AuditionFormValue): value is ConsentAuditionFormValue =>
    typeof value.answer !== 'string' && value.answer.type === AnswerFileType.CONSENT;
  const answerValues = values;
  const consentValues = values.filter((value) => isConsentAnswer(value));
  const { audition_id, examination_no, user_id_a, consent_version } = props;

  //１つのファイルサイズ500MB上限
  const maxFileSize = 500 * 1024 * 1024;
  for (const value of values) {
    if (
      typeof value.answer !== 'string' &&
      value.answer.file instanceof File &&
      value.answer.file.size > maxFileSize
    ) {
      throw new Error(`${value.apply_form_no} ファイルサイズは500MBまでです。`);
    }
    if (typeof value.answer !== 'string' && value.answer.file instanceof FileList) {
      Array.from(value.answer.file).map((file) => {
        if (file.size > maxFileSize) {
          throw new Error(`${value.apply_form_no}:${file.name} ファイルサイズは500MBまでです。`);
        }
      });
    }
  }

  /* 通常回答の変換 */
  const answerListTask = Promise.all(
    answerValues.map(async (value) => {
      if (typeof value.answer === 'string') {
        return {
          audition_id,
          examination_no,
          apply_form_no: parseInt(value.apply_form_no, 10),
          user_id_a,
          answer: value.answer,
          answer_etc: value.answer_etc,
          answer_kbn: value.answer_kbn,
        };
      }
      if (value.answer.type === AnswerFileType.CONSENT) {
        return {
          audition_id,
          examination_no,
          apply_form_no: parseInt(value.apply_form_no, 10),
          user_id_a,
          answer: '',
          answer_etc: value.answer_etc,
          answer_kbn: value.answer_kbn,
        };
      }
      const file = value.answer.file as File;
      const sanitizedFileName = sanitizeFileName(file.name);
      const { url, key } = await A07({
        upload_kbn: '8',
        audition_id,
        user_id: user_id_a,
        file_name: sanitizedFileName,
      });
      const isMp4orMov = sanitizedFileName.match(/\.(mp4|mov|MP4|MOV)$/i);
      await fetch(url, {
        method: 'PUT',
        body: file,
        ...(isMp4orMov ? { headers: { 'Cache-Control': 'no-store' } } : {}),
      });
      return {
        audition_id,
        examination_no,
        apply_form_no: parseInt(value.apply_form_no, 10),
        user_id_a,
        answer: key,
        answer_etc: value.answer_etc,
        answer_kbn: value.answer_kbn,
      };
    })
  );

  /* 同意書ファイルの変換 */
  const consentListTask = async () => {
    if (consentValues.length <= 0) return [];
    /* ０．回答にformat=20(同意書)が存在する場合、同意書保存用に同意IDを取得する。 */
    const { consent_id } = await A32({ user_id: user_id_a });
    const consent_lists: File[] = [];
    consentValues.map(async (value, i) => {
      Array.from(value.answer.file).map(async (file, j) => {
        consent_lists.push(file);
      });
    });
    return Promise.all(
      consent_lists.map(async (file, i) => {
        const renamedFileName = `${i + 1}.${file.name.split('.').at(-1)}`;
        const { url, key } = await A07({
          upload_kbn: '9',
          consent_id,
          user_id: user_id_a,
          file_name: renamedFileName,
        });
        await fetch(url, { method: 'PUT', body: file });
        return {
          consent_id, //０で取得した同意ID
          consenter: '1', //1(保護者) 固定 ※未成年の場合は保護者が登録するため
          consent_type: '9', //9(対象不明) 固定 ※国内用は同意書の区分無しのため
          audition_id, //URLより取得したオーディションID
          consent_version, //URLより取得した審査No
          file: key, //S3保存先のURL
          admin_approval: '9', //9(対象外)固定 国内向けは承認フローを経ないため
        };
      })
    );
  };
  const [answer_list, consent_list] = await Promise.all([answerListTask, consentListTask()]);
  return {
    answer_list,
    consent_list,
  };
};

/**
 * APIから取得したApplyFormのリストから、入力可能なApplyFormのみを取得する
 * ラベルや画像などの入力不可なApplyFormは除外する
 */
const notInputableApplyFormsFormatList = ['40', '41', '42', '53', '91', '92'];
export const filterInputableApplyForms = (applyForms: ApplyForm[]) =>
  applyForms.filter((form) => !notInputableApplyFormsFormatList.includes(form.format));

/* 応募フォーム表示時に海外ドメインの場合、/images/→/gl-images/に置き換えて配置 */
export function getImageSrcGlobal(src: string): string {
  const regex = /^\/images\//;
  const result = src.replace(regex, '/gl-images/');
  return result;
}

export const createPrefectures = (data: A04Output | null, isJapanese: boolean) => {
  if (!data) return [];
  return data.prefectures.map((prefecture) => {
    const value = prefecture.sno;
    const label = isJapanese ? prefecture.svalue : prefecture.svalue3;
    return {
      value,
      label,
    };
  });
};

export const createPreferredVenueList = (data: A04Output | null, isJapanese: boolean) => {
  if (!data) return [];
  return data.preferred_venue_list.map((venue) => {
    const value = String(venue.examination_s_no);
    const label = isJapanese ? venue.preferred_venue : venue.preferred_venue3;
    return {
      value,
      label,
    };
  });
};
